/* istanbul ignore file */
import _ from 'underscore';
import { getFormattedImage } from '@bingads-webui-campaign-react/copilot-common';
import { getCenterCroppedSettings } from '@bingads-webui-campaign/image-experience-utils';
import { getContrastRatio } from 'a11y-contrast-color';
import {
  AdSize,
  AdProps,
  TemplateConfig,
  TemplateConfigs,
  DisplayAds,
  AssetName,
  State,
  AssetProperties,
  TextAssetsFormatting,
  TextFormattingOption,
  BrandKitFont,
} from './types';
import { assetName as textAssetName } from './constants';

export const applyZoom = (length: number, zoomValue: string) => {
  const zoom = parseInt(zoomValue.replace('%', ''), 10) / 100;
  return Math.round(length * zoom);
};

// TODO: after MVP, if more sizes are supported they should be added here
export const sizeCategories = {
  Horizontal: ['970x250', '728x90', '320x50'],
  Vertical: ['160x600', '300x600'],
  Square: ['300x250'],
};

export const aspectRatioLogoMapping = {
  '1.91:1': 'landscape',
  '1:1': 'square',
  '4:1': 'landscape',
};
// TODO: WILL ADD CONFIG HERE
export const imageAspectRatioConfig = {
  '1.91:1': { type: 'Image191x100', minCropSize: [152, 80] },
  '1:1': { type: 'Image1x1', minCropSize: [80, 80] },
  '4:1': { type: 'Image4x1', minCropSize: [320, 80] },
};
export const getCropSetting = (image: any, aspectRatio) => {
  if (!aspectRatio || !image) {
    return {};
  }
  const cropSettings = getCenterCroppedSettings({
    SourceWidth: image.width,
    SourceHeight: image.height,
    SourceX: 0,
    SourceY: 0,
  }, aspectRatio);
  return cropSettings;
};

export const mapBrandKitImagesToDisplayAd = (brandKitImage, logoConfig: { aspectRatio: string } | null = null) => {
  if (!brandKitImage) {
    return {};
  }
  const data: any = {
    isBrandKitImage: true,
    url: brandKitImage.Src,
    assetId: brandKitImage.Id,
    imageSize: {
      width: brandKitImage.width || brandKitImage.CropWidth,
      height: brandKitImage.height || brandKitImage.CropHeight,
    },
  };
  if (logoConfig) {
    data.cropSetting = [{
      aspectRatio: logoConfig.aspectRatio,
      sourceHeight: brandKitImage.CropHeight,
      sourceWidth: brandKitImage.CropWidth,
      sourceX: brandKitImage.CropX,
      sourceY: brandKitImage.CropY,
    }]
  }
  return data;
};
export function getImageAssetConfigs(templateConfigs, targetSizes, assetType) {
  let display = false;
  let displayBackground= false;
  const apectRatioSet: Set<any> = new Set();
  targetSizes.forEach((size) => {
    const ad = templateConfigs[size];
    if (ad?.[assetType] && ad[assetType].display) {
      display = true;
      apectRatioSet.add(ad[assetType].aspectRatio);
    }
    if (ad?.background && ad.background.display) {
      displayBackground = true;
    }
  });
  return {
    display,
    displayBackground,
    apectRatioSet,
  };
}
export const filterBrandKitLogos = (brandKit, aspectRatio) => {
  const logoAspectRatio = aspectRatioLogoMapping[aspectRatio];
  if (!logoAspectRatio || !brandKit?.logos?.length) {
    return [];
  }
  const brandkitLogos = brandKit.logos.filter((logo) => logo.ImageType === logoAspectRatio);
  return brandkitLogos;
};

export const getBrandKitFilterLogos = (brandKit, templateConfigs) => {
  const size = Object.keys(templateConfigs)?.[0];
  const aspectRatio = templateConfigs[size]?.logo?.aspectRatio;
  const filterLogos = filterBrandKitLogos(brandKit, aspectRatio);
  return { filterLogos, aspectRatio };
};
export const addBrandKitToRecommendedAssets = (recommendedAssets, brandKit, templateConfigs) => {
  if (!brandKit) {
    return recommendedAssets;
  }
  const { filterLogos, aspectRatio } = getBrandKitFilterLogos(brandKit, templateConfigs);

  const theme = {
    images: [],
    logo: [],
  };
  const assets = recommendedAssets || {
    themesImages: [],
    themesLogos: [],
    headlines: [],
    longHeadlines: [],
    descriptions: [],
    imageAssets: [],
    logo: [],
    cta: [],
    color: [],
  };
  assets.themesImages = assets.themes || [];
  assets.themesLogos = [];
  if (assets?.logo?.length > 0) {
    assets.themesLogos.push({
      name: _TL_('Recommended Logos'),
      images: [...assets.logo],
    });
  }
  if (brandKit?.images?.length > 0) {
    assets.imageAssets = [...brandKit.images.map((image) => ({ ...mapBrandKitImagesToDisplayAd(image), ...getFormattedImage({ Url: image.Src }) })), ...assets.imageAssets];
    theme.images = brandKit.images.map((image) => ({ ...mapBrandKitImagesToDisplayAd(image), ...getFormattedImage({ Url: image.Src }) }));
  }
  // pick one square or landscape image for logo
  if (filterLogos?.length > 0) {
    assets.logo = [...filterLogos.map((logo) => ({ ...mapBrandKitImagesToDisplayAd(logo, { aspectRatio }), ...getFormattedImage({ Url: logo.Src }) })), ...assets.logo];
    theme.logo = filterLogos.map((logo) => ({ ...mapBrandKitImagesToDisplayAd(logo, { aspectRatio }), ...getFormattedImage({ Url: logo.Src }) }));
  }
  if (brandKit?.palettes?.length > 0) {
    assets.color = brandKit.palettes.map((palette) => ({ ...palette, isBrandKitColor: true }));
  }

  assets.themesImages.push({
    name: _TL_('Brand Kit'),
    images: [...theme.images],
  });
  assets.themesLogos.push({
    name: _TL_('Brand Kit'),
    images: [...theme.logo],
  });
  return assets;
};

export const getAdSizeCategory = (size: AdSize) => Object.keys(sizeCategories)
  .find(category => sizeCategories[category].includes(size)) || 'Other';

export const getAdConfig = (ad: AdProps, templateConfig: TemplateConfig) => ({
  template: {
    ..._.pick(templateConfig, 'templateId', 'templateName', 'version', 'style'),
    ...((templateConfig.shortHeadline && templateConfig.shortHeadline.display)? { shortHeadline: _.pick(ad.shortHeadline, 'text') }: {}),
    ...((templateConfig.longHeadline && templateConfig.longHeadline.display)? { longHeadline: _.pick(ad.longHeadline, 'text') }: {}),
    ...((templateConfig.description && templateConfig.description.display)? { description: _.pick(ad.description, 'text') }: {}),
    ...((templateConfig.cta && templateConfig.cta.display)? { cta: _.pick(ad.cta, 'text') }: {}),
    ...((templateConfig.image && templateConfig.image.display)? { image: _.pick(ad.image, 'url', 'cropSetting') }: {}),
    ...((templateConfig.logo && templateConfig.logo.display)? { logo: _.pick(ad.logo, 'url', 'cropSetting') }: {}),
    ...((templateConfig.color && templateConfig.color)? { color: ad.color }: {}),
    assetProperties: ad.assetProperties,
    background: ad.background,
  },
});

// get ads configs to get ad images
export const getAdConfigs = (displayAds: DisplayAds, templateConfigs: TemplateConfigs) => {
  const ads = Object.keys(displayAds).map((size) => {
    const ad = displayAds[size];
    const templateConfig = templateConfigs[size];

    return getAdConfig(ad, templateConfig);
  });

  return ads;
};

// get text assets formatting config for a given asset
export const getTextFormattingOptions = (
  displayAds: State['displayAds'],
  targetSizes: State['targetSizes'],
  prop: AssetName
) => {
  const textFormattingColorOptions: Set<any> = new Set();
  const textFormattingFontOptions: Set<any> = new Set();
  const textFormattingWeightOptions: Set<any> = new Set();

  targetSizes.forEach((size) => {
    const ad = displayAds[size];

    if (!ad?.assetProperties) return;

    const selectedColor = ad.assetProperties.find(({ assetName, assetProperty }) =>
      assetName === prop && assetProperty === 'Color')?.value;
    if (selectedColor) {
      textFormattingColorOptions.add(selectedColor);
    }

    const selectedFont = ad.assetProperties.find(({ assetName, assetProperty }) =>
      assetName === prop && assetProperty === 'FontFamily')?.value;
    if (selectedFont) {
      textFormattingFontOptions.add(selectedFont);
    }

    const selectedWeight = ad.assetProperties.find(({ assetName, assetProperty }) =>
      assetName === prop && assetProperty === 'FontWeight')?.value;
    if (selectedWeight) {
      textFormattingWeightOptions.add(selectedWeight);
    }
  });

  return {
    font: {
      value:
        textFormattingFontOptions.size === 1
          ? Array.from(textFormattingFontOptions)[0] || ''
          : '',
      options: Array.from(textFormattingFontOptions),
      isMultiple: textFormattingFontOptions.size > 1,
    },
    weight: {
      value:
        textFormattingWeightOptions.size === 1
          ? Array.from(textFormattingWeightOptions)[0] || ''
          : '',
      options: Array.from(textFormattingWeightOptions),
      isMultiple: textFormattingWeightOptions.size > 1,
    },
    color: {
      value:
        textFormattingColorOptions.size === 1
          ? Array.from(textFormattingColorOptions)[0] || ''
          : '',
      options: Array.from(textFormattingColorOptions),
      isMultiple: textFormattingColorOptions.size > 1,
    },
  };
};

export const getAllTextFormattingOptions = (
  displayAds: State['displayAds'],
  targetSizes: State['targetSizes']
): TextAssetsFormatting => {
  const shortHeadlineFormattingOptions = getTextFormattingOptions(
    displayAds,
    targetSizes,
    'ShortHeadline'
  );
  const longHeadlineFormattingOptions = getTextFormattingOptions(
    displayAds,
    targetSizes,
    'LongHeadline'
  );
  const descriptionFormattingOptions = getTextFormattingOptions(
    displayAds,
    targetSizes,
    'Description'
  );
  const callToActionFormattingOptions = getTextFormattingOptions(
    displayAds,
    targetSizes,
    'CTA'
  );

  return {
    [textAssetName.shortHeadline]: {
      font: shortHeadlineFormattingOptions.font.value,
      color: shortHeadlineFormattingOptions.color.value,
      weight: shortHeadlineFormattingOptions.weight.value,
    },
    [textAssetName.longHeadline]: {
      font: longHeadlineFormattingOptions.font.value,
      color: longHeadlineFormattingOptions.color.value,
      weight: longHeadlineFormattingOptions.weight.value,
    },
    [textAssetName.description]: {
      font: descriptionFormattingOptions.font.value,
      color: descriptionFormattingOptions.color.value,
      weight: descriptionFormattingOptions.weight.value,
    },
    [textAssetName.callToAction]: {
      font: callToActionFormattingOptions.font.value,
      color: callToActionFormattingOptions.color.value,
      weight: callToActionFormattingOptions.weight.value,
    },
  };
};

export const getTransformedAssetProperties = (formatConfig: TextFormattingOption, section: AssetName): AssetProperties[] => {
  const assetProperties: AssetProperties[] = [];
  const { font, color, weight } = formatConfig;

  if (font) {
    assetProperties.push({
      assetName: section,
      assetProperty: 'FontFamily',
      value: font,
    });
  }

  if (color) {
    assetProperties.push({
      assetName: section,
      assetProperty: 'Color',
      value: color,
    });
  }

  if (weight) {
    assetProperties.push({
      assetName: section,
      assetProperty: 'FontWeight',
      value: weight,
    });
  }

  return assetProperties;
};

export const getAssetPropertiesFromBrandKitFonts = (brandKitFonts: BrandKitFont[]) : AssetProperties[] => {
  const assetProperties: AssetProperties[] = [];

  brandKitFonts.forEach(({ Typeface, Weight, TextAssetType }) => {
    const assetName = TextAssetType === 'CallToAction' ? 'CTA' : TextAssetType;
    if (Typeface) {
      assetProperties.push({
        assetName,
        value: Typeface,
        assetProperty: 'FontFamily',
      });
    }

    if (Weight) {
      assetProperties.push({
        assetName,
        value: Weight,
        assetProperty: 'FontWeight',
      });
    }
  });

  return assetProperties;
};

export const getNewAssetProperties = (assetProperties: AssetProperties[], payload: AssetProperties[]) => {
  const newAssetProperties = [...assetProperties];

  payload.forEach((newProp) => {
    const index = newAssetProperties.findIndex(({ assetName, assetProperty }) =>
      assetName === newProp.assetName && assetProperty === newProp.assetProperty);

    if (index === -1) {
      newAssetProperties.push(newProp);
    } else {
      newAssetProperties[index] = newProp;
    }
  });

  return newAssetProperties;
};

export const getColorContrastRatio = (foregrounds: string[], background: string) => {
  const hexToRgb = (hex: string): [number, number, number] | null => {
    // convert shorthand (ex '123') to full (ex '112233')
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const hexCopy = hex.replace(shorthandRegex, (m, r, g, b) => `${r}${r}${g}${g}${b}${b}`);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexCopy);

    return result
      ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
      : null;
  };

  const ratios : number[] = [];
  foregrounds.forEach((foreground) => {
    const rgb1 = hexToRgb(foreground);
    const rgb2 = hexToRgb(background);

    if (rgb1 && rgb2) {
      // range of 0 to 21, 4.5 being the minumum for readability
      ratios.push(getContrastRatio(rgb1, rgb2));
    } else {
      ratios.push(0);
    }
  });

  if (_.some(ratios, ratio => ratio < 4.5)) {
    return 0;
  }

  let sum = 0;
  ratios.forEach((element) => {
    sum += element;
  });

  return sum / ratios.length;
};
